import React, { PropsWithChildren } from 'react';
import isPropValid from '@emotion/is-prop-valid';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiButton from '@mui/material/Button';
import MuiDrawer from '@mui/material/Drawer';
import MuiGrid from '@mui/material/Grid';
import type { Locale } from '@yi/core';

import { OPTIMIZELY_ID_ATTR } from '~/src/common/optimizely';
import {
  darkGrey,
  ebony,
  sideNavWidth,
  sizeDesktopLg,
  sizeMobile,
  turquoise,
  playlistHeight,
  expandedPlaylistHeight,
  leftPanelZIndex,
  breakpoints,
} from '~/styles';

import type { HeaderType } from './MainLayout';

import { IconLogo, IconLogoText } from '../icons';
import { LocalizedLink } from '../LocalizedLink';

export const Grid = styled(MuiGrid)`
  display: flex;
  &:empty {
    display: none;
  }
`;

export const NavList = styled.div<{ locale: Locale }>`
  margin: 0;
  padding: 0;
  list-style: none;
  list-style-type: none;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: end;
  -ms-flex-pack: end;
  justify-content: flex-end;
  text-wrap: ${({ locale }) => (locale === 'en' ? 'nowrap' : '')};
`;

export const ItemWrapper = styled.div<{ marginLeft?: boolean }>`
  ${props =>
    props.marginLeft &&
    css`
      margin-left: 10px;
    `};
`;

const BaseLogoAnchor: React.FC<React.ComponentProps<typeof LocalizedLink>> = ({ ...props }) => (
  <LocalizedLink {...props} />
);
export const LogoAnchor = styled(BaseLogoAnchor, {
  shouldForwardProp: prop => isPropValid(prop) && prop !== 'marginRight',
})<{ marginRight?: boolean }>`
  text-decoration: none;
  ${props =>
    props.marginRight &&
    css`
      margin-right: 10px;
      ${sizeMobile(css`
        margin-right: 5px;
      `)};
    `};
`;

const BaseIconLogoText: React.FC<React.ComponentProps<typeof IconLogoText>> = ({ ...props }) => (
  <IconLogoText {...props} />
);
export const IconLogoTextResponsive = styled(BaseIconLogoText)`
  ${css`
    @media screen and (max-width: 525px) {
      width: 130px;
    }

    @media screen and (max-width: 454px) {
      width: 115px;
    }
  `}
  width: 215px;
`;

export const IconLogoResponsive = styled(IconLogo, {
  shouldForwardProp: prop => prop !== 'smallSize',
})`
  width: 26px;
  height: 26px;
`;
interface DrawerProps {
  playlistEnabled?: boolean;
  playlistDetailView?: boolean;
}

export const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => !['playlistEnabled', 'playlistDetailView'].includes(prop),
})<DrawerProps>`
  .MuiDrawer-paper {
    margin-top: var(--Header-Height);
    z-index: ${leftPanelZIndex};
    ${props =>
      props.playlistEnabled
        ? props.playlistDetailView
          ? css`
              height: calc(100% - (${expandedPlaylistHeight} + var(--Header-Height)));
            `
          : css`
              height: calc(100% - (${playlistHeight} + var(--Header-Height)));
            `
        : css`
            height: calc(100% - var(--Header-Height));
          `};
    padding: 0 0 20px;
    overflow-y: auto;
    background: ${darkGrey};
  }
  .MuiDrawer-paperAnchorDockedLeft {
    border-right: none;
  }
`;

export const Accordion = styled(MuiAccordion)`
  box-shadow: none;
  background-color: transparent;
  &:before {
    background-color: transparent;
  }
  &.Mui-expanded {
    margin: 0;
  }
  ${sizeMobile(css`
    min-width: 50%;
  `)}
` as React.FC<PropsWithChildren<React.ComponentProps<typeof MuiAccordion>>>;

export const AccordionDetails = styled(MuiAccordionDetails)`
  padding: 0;
  display: block;
  background-color: ${darkGrey};
` as React.FC<PropsWithChildren<React.ComponentProps<typeof MuiAccordionDetails>>>;

export const AccordionSummary = styled(MuiAccordionSummary)`
  padding: 0;
  background-color: ${darkGrey};
  .MuiAccordionSummary-content {
    margin: 0;
    display: block;
  }
  &.Mui-expanded {
    min-height: 0;
  }
` as React.FC<PropsWithChildren<React.ComponentProps<typeof MuiAccordionSummary>>>;

export const StyledLocalizedLink = styled(LocalizedLink)`
  position: relative;
  &:hover {
    text-decoration: none;
  }
  &:first-of-type div {
    &::before {
      content: '';
      position: absolute;
      height: 1px;
      background: ${ebony};
      width: 91%;
      right: 0;
      top: -2px;
    }
  }
`;

export const NewItems = styled(MuiButton)`
  margin-left: auto;
  margin-top: -6px;
  color: ${turquoise};
  &:hover {
    color: white;
    text-decoration: none;
  }
`;

export const MenuContainer = styled('div', {
  shouldForwardProp: prop => !['showSideNav', 'hideDefaultSideNav'].includes(prop),
})<{ showSideNav?: boolean; hideDefaultSideNav?: boolean }>`
  height: 100%;
  padding-top: 30px;
  width: 75px;
  display: none;
  background: ${darkGrey};
  overflow-y: scroll;
  overflow-x: hidden;
  overscroll-behavior: none;
  -ms-scroll-chaining: none; /* overscroll-behavior for IE */
  scrollbar-color: ${darkGrey} ${darkGrey};
  scrollbar-width: none;
  transition: all 100ms ease-in-out;
  &::-webkit-scrollbar {
    width: 0px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${darkGrey};
  }
  ${props =>
    props.showSideNav &&
    css`
      display: block;
      width: 270px;
      transition: all 100ms ease-in-out;
    `};
  ${({ hideDefaultSideNav }) =>
    !hideDefaultSideNav &&
    css`
      ${sizeDesktopLg(css`
        display: block;
      `)};
    `};
  ${props =>
    props.showSideNav &&
    sizeMobile(css`
      width: 100%;
      height: 100%;
      overflow: scroll;
    `)}
`;

export const Menu = styled.div`
  height: 100%;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  flex-direction: column;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  letter-spacing: 2px;
  ${sizeMobile(css`
    height: auto;
    flex-direction: row;
    flex-wrap: wrap;
    a {
      width: 50%;
    }
  `)}
`;

const MenuItemStyles = css`
  display: flex;
  justify-content: flex-start;
  border-radius: unset;
  width: 100%;
  letter-spacing: 2px;
  font-weight: 400;
`;

export const MenuItem = styled(MuiButton)`
  ${MenuItemStyles}
  padding: 17px 15px 11px 25px;
  cursor: pointer;
  position: relative;
  &:hover {
    background: ${turquoise};
    button {
      color: white;
    }
  }
  margin-bottom: 1px;
  &::after {
    content: '';
    position: absolute;
    height: 1px;
    background: ${ebony};
    width: 91%;
    right: 0;
    bottom: -1px;
  }
  ${sizeMobile(css`
    letter-spacing: 0;
    padding: 20px 16px;
    align-items: center;
    &::after {
      display: none;
    }
  `)}
`;

interface Props {
  showSideNav?: boolean;
}

const BaseMuiButton: React.FC<React.ComponentProps<typeof MuiButton>> = props => (
  <MuiButton {...props} />
);
export const LanguageMenuItem: React.FC<PropsWithChildren<Props>> = styled(BaseMuiButton, {
  shouldForwardProp: prop => !['showSideNav'].includes(prop),
})<Props>`
  ${MenuItemStyles}
  padding: 12px 25px;
  cursor: pointer;
  position: relative;
  &:hover {
    background: ${turquoise};
  }
  display: none;
  ${props =>
    props.showSideNav &&
    css`
      display: flex;
    `};
  position: relative;
  margin-bottom: 1px;
  &::after {
    content: '';
    position: absolute;
    height: 1px;
    background: ${ebony};
    width: 91%;
    right: 0;
    bottom: -1px;
  }
  ${sizeMobile(css`
    letter-spacing: 0;
    align-items: center;
    &::after {
      display: none;
    }
  `)}
` as React.FC<PropsWithChildren<Props & React.ComponentProps<typeof MuiButton>>>;

export const ItemText = styled('span', {
  shouldForwardProp: prop => isPropValid(prop) || prop === OPTIMIZELY_ID_ATTR,
})<{ fadeRight?: boolean }>`
  text-align: left;
  font-size: 14px;
  top: unset;
  margin: 0 0 0 30px;
  font-weight: 500;
  text-transform: capitalize;
  ${breakpoints.minMobileLandscape} {
    text-transform: uppercase;
    font-size: 0.7875rem;
    margin: 0 0 0 45px;
  }
  opacity: 0;
  color: #fff;
  left: 25px;
  position: absolute;
  top: 19px;
  ${({ fadeRight }) =>
    fadeRight
      ? `
          opacity: 1;
          transition: all 200ms linear;
        `
      : ''};
`;

export const MenuFooter = styled.div`
  margin-top: auto;
  margin-bottom: 80px;
  ${sizeMobile(css`
    display: flex;
    align-items: flex-start;
    width: 100%;
  `)}
`;
export const ChildrenContainer = styled.div<{ headerType?: HeaderType }>`
  padding-top: ${({ headerType }) => (headerType === 'hideTopNav' ? 0 : 'var(--Header-Height)')};
  ${({ headerType }) =>
    headerType !== 'hideDefaultSideNav' &&
    headerType !== 'hideTopNav' &&
    headerType !== 'simpleNavWithSignIn' &&
    css`
      ${sizeDesktopLg(css`
        padding-left: ${sideNavWidth};
      `)};
    `};
`;

// export const StyledLiveChip = styled(LiveChip)`
//   right: 0;
//   margin-left: 10px;
//   z-index: 0;
//   position: relative;
//   padding: 3px 11px;
//   bottom: 2px;
// `;
